import { useLayoutEffect, useRef, useState } from 'react'
import type { RefObject } from 'react'

interface UseDynamicFontSizeParams {
  targetRef: RefObject<HTMLElement>
  containerRef?: RefObject<HTMLElement>
  contentKey: string | number
  maxFontSize: string
  minFontSize: string
}

const SHRINK_RATIO = 0.97
const MAX_ITERATIONS = 24
const SAFE_PADDING_BUFFER = 4
const FIT_TOLERANCE = 2

const toPixels = (value: string): number => {
  const numeric = parseFloat(value)
  if (Number.isNaN(numeric)) return 0
  if (value.toLowerCase().includes('rem') || value.toLowerCase().includes('em')) {
    return numeric * 16
  }
  return numeric
}

/**
 * Shrinks text until it fits its container while respecting the configured min/max.
 * Runs only when the provided content key changes (ex: new word).
 */
export function useDynamicFontSize({
  targetRef,
  containerRef,
  contentKey,
  maxFontSize,
  minFontSize,
}: UseDynamicFontSizeParams) {
  const [computedSize, setComputedSize] = useState(maxFontSize)
  const rafRef = useRef<number | null>(null)
  const lastAppliedSizeRef = useRef<number>(-1)
  const debounceRef = useRef<number | null>(null)

  useLayoutEffect(() => {
    const target = targetRef.current
    if (!target) return

    const container = containerRef?.current || target
    const maxPx = toPixels(maxFontSize)
    const minPx = Math.min(toPixels(minFontSize) || maxPx, maxPx)

    if (!maxPx) {
      setComputedSize(maxFontSize)
      return
    }

    const adjustSize = () => {
      let nextSize = maxPx
      target.style.fontSize = `${nextSize}px`

      const fitsInContainer = () => {
        const safeWidth = Math.max(0, container.clientWidth - SAFE_PADDING_BUFFER - FIT_TOLERANCE)
        const safeHeight = Math.max(0, container.clientHeight - SAFE_PADDING_BUFFER - FIT_TOLERANCE)
        return target.scrollWidth <= safeWidth && target.scrollHeight <= safeHeight
      }

      let iterations = 0
      while (!fitsInContainer() && nextSize > minPx && iterations < MAX_ITERATIONS) {
        nextSize = Math.max(minPx, nextSize * SHRINK_RATIO)
        target.style.fontSize = `${nextSize}px`
        iterations += 1
      }

      // If we still don't fit (e.g., oscillating around a boundary), step down once and stop.
      if (!fitsInContainer()) {
        nextSize = Math.max(minPx, nextSize * 0.95)
        target.style.fontSize = `${nextSize}px`
      }

      if (lastAppliedSizeRef.current === nextSize) return
      lastAppliedSizeRef.current = nextSize
      setComputedSize(`${nextSize}px`)
    }

    const scheduleAdjust = () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
      debounceRef.current = window.setTimeout(() => {
        rafRef.current = requestAnimationFrame(adjustSize)
      }, 30)
    }

    // Adjust on next frame to ensure layout has settled
    scheduleAdjust()

    const handleResize = () => scheduleAdjust()
    window.addEventListener('resize', handleResize, { passive: true })

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current)
      }
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
      window.removeEventListener('resize', handleResize)
    }
  }, [contentKey, maxFontSize, minFontSize, targetRef, containerRef])

  return computedSize
}
